import { scoredConfidence } from '@authe/fe-commons/attribution';
import { chainDisplayName } from '@authe/fe-commons/chains';
import { formatTimestamp } from '@authe/fe-commons/time';
import { asTrimmedString } from '@authe/fe-commons/display';
import * as React from 'react';
import { Badge, AddressDisplay, AddressTypeBadge, PrimaryChainBadge, LifecycleBadge, RiskBadge, EntityCard, ChainAvatar, CatalogLabel, Tag, GroupBadge, AlertIcon, ExternalLinkIcon, Tooltip, TooltipTrigger, TooltipContent, badgeVariants } from '@authe/ui-core';
import { RiskLevel, Severity } from '@authe/fe-commons/precedence';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

// src/review/types.ts
var REVIEW_STATUSES = ["pending", "approved", "rejected", "escalated"];
var REVIEW_STATUS_FILTERS = [...REVIEW_STATUSES, "all"];
var REVIEW_TARGET_KINDS = [
  "address",
  "token",
  // A market snapshot beside a token definition, keyed coin_id + source. Its
  // own kind because filing it as `token` put the price and the definition in
  // one bucket — the queue showed two "Token" rows per token with no way to
  // tell which was which.
  "asset_ticker",
  "nft",
  "abi",
  "proxy",
  "entity",
  "label",
  "tag",
  "group",
  "entity_mapping",
  "assignment",
  "catalog_field",
  "ml_prediction"
];
var REVIEW_REASONS = [
  "low_confidence",
  "human_overwrite",
  "conflict",
  "quarantine",
  "rotation",
  "hitl",
  "trigger"
];

// src/review/wire.ts
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function record(value) {
  return isRecord(value) ? value : {};
}
function text(value, fallback = "") {
  return typeof value === "string" ? value : value == null ? fallback : String(value);
}
function textOrNull(value) {
  return value == null ? null : String(value);
}
function num(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}
function strings(value) {
  if (typeof value === "string") return value.trim() ? [value] : [];
  return Array.isArray(value) ? value.filter((entry) => typeof entry === "string" && !!entry.trim()) : [];
}
function oneOf(value, allowed, fallback) {
  return allowed.includes(text(value)) ? value : fallback;
}
function rows(value) {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}
function evidenceSourceFromWire(raw) {
  return {
    kind: text(raw.kind, "unknown") || "unknown",
    id: textOrNull(raw.id),
    confidence: num(raw.confidence),
    asserted_fields: strings(raw.asserted_fields),
    conflict: raw.conflict === true,
    reasons: strings(raw.reasons),
    url: textOrNull(raw.url),
    last_seen_at: num(raw.last_seen_at),
    age_days: num(raw.age_days),
    // `explain_evidence` returns a list, but the field crosses a JSON boundary
    // off a persisted row; a bare string is one line, not one line per letter.
    explain: strings(raw.explain)
  };
}
function reviewEvidenceFromWire(raw) {
  if (!isRecord(raw)) return null;
  const sources = rows(raw.sources).map(evidenceSourceFromWire);
  const hasAnything = sources.length > 0 || raw.base_confidence != null || raw.headline_confidence != null || Boolean(raw.explanation);
  if (!hasAnything) return null;
  return {
    sources,
    distinct_sources: num(raw.distinct_sources),
    conflicts: num(raw.conflicts),
    base_confidence: num(raw.base_confidence),
    agreement_delta: num(raw.agreement_delta),
    headline_confidence: num(raw.headline_confidence),
    explanation: raw.explanation ? text(raw.explanation) : null
  };
}
function reviewItemFromWire(raw) {
  const row = record(raw);
  return {
    review_id: text(row.review_id),
    status: oneOf(row.status, REVIEW_STATUSES, "pending"),
    target_kind: oneOf(row.target_kind, REVIEW_TARGET_KINDS, "catalog_field"),
    target_id: text(row.target_id),
    org_id_tenant_id: textOrNull(row.org_id_tenant_id),
    reason: oneOf(row.reason, REVIEW_REASONS, "low_confidence"),
    reason_detail: text(row.reason_detail),
    confidence: num(row.confidence),
    providers: strings(row.providers),
    evidence: reviewEvidenceFromWire(row.evidence),
    field_path: textOrNull(row.field_path),
    existing_value: row.existing_value ?? null,
    proposed_value: row.proposed_value ?? null,
    payload: record(row.payload),
    payload_table: textOrNull(row.payload_table),
    source: textOrNull(row.source),
    asserted_at: num(row.asserted_at),
    updated_at: num(row.updated_at),
    reviewer_id: textOrNull(row.reviewer_id),
    review_notes: textOrNull(row.review_notes),
    metadata: record(row.metadata)
  };
}
function trustTier(confidence) {
  const n = confidence ?? 0;
  if (n >= 0.8) return "high";
  if (n >= 0.6) return "medium";
  if (n >= 0.45) return "low";
  return "weak";
}
var STALE_AFTER_DAYS = 180;
function isStale(source) {
  return (source.age_days ?? 0) >= STALE_AFTER_DAYS;
}
function hasEvidence(evidence) {
  return !!evidence && (evidence.sources?.length ?? 0) > 0;
}
function explainOf(source) {
  const raw = source.explain;
  if (Array.isArray(raw)) return raw.filter((line) => typeof line === "string" && !!line.trim());
  if (typeof raw === "string" && raw.trim()) return [raw];
  return [];
}
var UNSCORED_REASON_TEXT = {
  "no-evidence": "no evidence recorded",
  "schema-default": "the schema's default, not a score",
  absent: "not recorded"
};
function summariseEvidence(evidence, confidence) {
  const sources = evidence?.sources ?? [];
  const headline = evidence?.headline_confidence ?? void 0;
  const score = headline ?? confidence ?? void 0;
  const base = evidence?.base_confidence ?? void 0;
  const delta = evidence?.agreement_delta ?? 0;
  let unscored = null;
  if (score == null) unscored = "absent";
  else if (!hasEvidence(evidence) && headline == null) unscored = "no-evidence";
  else if (scoredConfidence(score) === void 0) unscored = "schema-default";
  return {
    score,
    unscored,
    sourceCount: evidence?.distinct_sources ?? sources.length,
    conflicts: evidence?.conflicts ?? 0,
    base,
    delta,
    hasArithmetic: base != null && headline != null,
    // The backend sorts strongest-first; don't re-sort and risk disagreeing
    // with the order the panel lists.
    strongest: sources[0],
    sources,
    explanation: evidence?.explanation ?? void 0,
    hasBreakdown: hasEvidence(evidence)
  };
}

// src/review/rowDiff.ts
function names(metadata, key) {
  if (!metadata || typeof metadata !== "object") return [];
  const raw = metadata[key];
  return Array.isArray(raw) ? raw.filter((v) => typeof v === "string") : [];
}
function asRecord(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}
function rowDiff(item) {
  const existing = asRecord(item.existing_value);
  const payload = asRecord(item.payload) ?? {};
  const omitted = names(item.metadata, "existing_omitted");
  const preserved = names(item.metadata, "existing_preserved");
  if (!existing && omitted.length === 0 && preserved.length === 0) {
    return { changes: [], preserved: [], unknown: true };
  }
  const changes = [
    ...Object.keys(existing ?? {}).map((field) => ({
      field,
      existing: existing[field],
      proposed: payload[field],
      omitted: false
    })),
    // Named but not valued: the live ABI body isn't going in a diff box, but the
    // reviewer still has to know approving replaces it.
    ...omitted.map((field) => ({ field, proposed: payload[field], omitted: true }))
  ].sort((a, b) => a.field.localeCompare(b.field));
  return { changes, preserved: [...preserved].sort(), unknown: false };
}
var CATALOG_WRITE_TABLES = {
  EntitiesDynamoDBTableName: { label: "Entity", kind: "entity" },
  LabelsDynamoDBTableName: { label: "Label", kind: "label" },
  TagsDynamoDBTableName: { label: "Tag", kind: "tag" },
  GroupsDynamoDBTableName: { label: "Group", kind: "group" },
  CatalogAssignmentsByTargetDynamoDBTableName: { label: "Assignment", kind: "assignment" },
  AddressEntityMappingsDynamoDBTableName: { label: "Entity mapping", kind: "entity_mapping" }
};
var TENANCY_FIELDS = /* @__PURE__ */ new Set([
  "customer_id",
  "org_id",
  "tenant_id",
  "user_id",
  "org_id_tenant_id",
  "api_key_id",
  "owner_user_id",
  "visible_to_user_ids",
  "visible_to_team_ids"
]);
var TENANCY_CHECKED = ["customer_id", "org_id", "tenant_id", "user_id"];
var BOOKKEEPING_FIELDS = /* @__PURE__ */ new Set([
  "id",
  "created_at",
  "updated_at",
  "ttl",
  "created_by",
  "created_by_service",
  "updated_by",
  "updated_by_service",
  "name_lower",
  // A copy of `valid_from` (or the write time) kept for the reverse-time index.
  "sort_ts",
  "evidence",
  "evidence_refs"
]);
var KEY_SHAPED_FIELD = /(?:^|_)(?:pk|sk)(?:_v\d+)?$|^gsi\d+_?(?:pk|sk)$/;
var CHAIN_FIELDS = /* @__PURE__ */ new Set(["chain", "chain_id", "applies_to_chains", "primary_chains"]);
var TIMESTAMP_FIELDS = /* @__PURE__ */ new Set(["asserted_at", "valid_from", "valid_to", "superseded_at"]);
var MILLISECOND_TIMESTAMP_FLOOR = 1e12;
var REFERENCE_FIELDS = /* @__PURE__ */ new Set([
  "catalog_id",
  "entity_id",
  "parent_entity_id",
  "related_entity_ids",
  "member_entity_ids",
  "member_label_ids",
  "member_tag_ids"
]);
var FIELD_ORDER = [
  "name",
  "entity_type",
  "category",
  "description",
  "address",
  "chain",
  "chain_id",
  "applies_to_chains",
  "primary_chains",
  "scope",
  "scope_target_id",
  "catalog_kind",
  "catalog_id",
  "entity_id",
  "role",
  "tier",
  "risk_level",
  "severity",
  "lifecycle",
  "confidence",
  "is_human_approved",
  "system_default"
];
var TENANCY_ORDER = ["customer_id", "org_id", "tenant_id", "user_id"];
var FIELD_LABEL = {
  entity_type: "Type",
  chain_id: "Chain",
  scope_target_id: "Target",
  catalog_id: "Catalog row",
  entity_id: "Entity",
  parent_entity_id: "Parent entity",
  tier: "Member tier",
  is_human_approved: "Human approved",
  logo_s3_uri: "Logo",
  explanation: "Why",
  window_keyed: "Window-keyed"
};
function hasOwn(record2, key) {
  return Object.prototype.hasOwnProperty.call(record2, key);
}
function formatFieldValue(value) {
  if (value === void 0) return "\u2014";
  if (value === null) return "null";
  if (typeof value === "string") return value === "" ? '""' : value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
function fieldLabel(field) {
  if (hasOwn(FIELD_LABEL, field)) return FIELD_LABEL[field];
  const words = field.replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : field;
}
function isEmptyValue(value) {
  if (value == null || value === "") return true;
  if (Array.isArray(value)) return value.length === 0;
  return isRecord(value) && Object.keys(value).length === 0;
}
function orderBy(order) {
  return (a, b) => {
    const ia = order.indexOf(a.field);
    const ib = order.indexOf(b.field);
    if (ia !== ib) return (ia === -1 ? order.length : ia) - (ib === -1 ? order.length : ib);
    return a.field.localeCompare(b.field);
  };
}
function displayFieldValue(field, value, names2 = /* @__PURE__ */ new Map()) {
  if (field === "applies_to_chains" && Array.isArray(value) && value.length === 0) {
    return "All chains";
  }
  if (TIMESTAMP_FIELDS.has(field) && typeof value === "number" && Number.isFinite(value) && value > 0) {
    return formatTimestamp(value, {
      unit: value >= MILLISECOND_TIMESTAMP_FLOOR ? "milliseconds" : "seconds"
    });
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  const one = (entry) => {
    if (typeof entry === "string" && entry) {
      if (CHAIN_FIELDS.has(field)) return chainDisplayName(entry) || entry;
      const ref = REFERENCE_FIELDS.has(field) ? names2.get(entry) : void 0;
      if (ref) return ref;
    }
    return formatFieldValue(entry);
  };
  if (Array.isArray(value) && value.every((entry) => entry === null || typeof entry !== "object")) {
    return value.map(one).join(", ");
  }
  return one(value);
}
function rowFields(row, names2 = /* @__PURE__ */ new Map()) {
  const fields = [];
  const hidden = [];
  for (const [field, value] of Object.entries(row ?? {})) {
    if (value === void 0) continue;
    const entry = { field, label: fieldLabel(field), value };
    const reason = TENANCY_FIELDS.has(field) ? "tenancy" : BOOKKEEPING_FIELDS.has(field) || KEY_SHAPED_FIELD.test(field) ? "bookkeeping" : isEmptyValue(value) && field !== "applies_to_chains" ? "empty" : null;
    if (reason) hidden.push({ ...entry, display: formatFieldValue(value), reason });
    else fields.push({ ...entry, display: displayFieldValue(field, value, names2) });
  }
  fields.sort(orderBy(FIELD_ORDER));
  hidden.sort((a, b) => a.field.localeCompare(b.field));
  return { fields, hidden };
}
function catalogBundle(item) {
  if (item.field_path) return null;
  const rawWrites = item.payload?.writes;
  if (!Array.isArray(rawWrites)) return null;
  const tenancy = isRecord(item.payload.tenancy) ? item.payload.tenancy : null;
  const entries = rawWrites.map((write) => {
    const table = isRecord(write) && typeof write.table === "string" ? write.table : "";
    const row = isRecord(write) && isRecord(write.item) ? write.item : null;
    const known = hasOwn(CATALOG_WRITE_TABLES, table) ? CATALOG_WRITE_TABLES[table] : null;
    return { table, row, known };
  });
  const names2 = /* @__PURE__ */ new Map();
  for (const { row, known } of entries) {
    if (!row || typeof row.id !== "string" || typeof row.name !== "string" || !row.name) continue;
    const kind = known ? known.label.toLowerCase() : "row";
    names2.set(row.id, `${row.name} (${kind} in this proposal)`);
  }
  const writes = entries.map(({ table, row, known }) => {
    const tenancyMismatch = row && tenancy ? TENANCY_CHECKED.filter((key) => (row[key] ?? null) !== (tenancy[key] ?? null)) : [];
    return {
      table,
      label: known ? known.label : table || "Unknown table",
      kind: known ? known.kind : null,
      row,
      ...rowFields(row, names2),
      tenancyMismatch
    };
  });
  const tenancyFields = Object.entries(tenancy ?? {}).filter(([, value]) => value !== void 0).map(([field, value]) => ({
    field,
    label: fieldLabel(field),
    value,
    display: formatFieldValue(value)
  })).sort(orderBy(TENANCY_ORDER));
  return {
    writes,
    tenancy: tenancyFields,
    names: names2,
    readable: writes.some((write) => write.fields.length > 0)
  };
}
function bareName(label) {
  return label?.replace(/ \([^()]* in this proposal\)$/, "");
}
function subjectRow(item) {
  const bundle = catalogBundle(item);
  if (!bundle) return item.payload ?? {};
  const subject = bundle.writes.find((write) => write.row && write.kind === item.target_kind) ?? bundle.writes.find((write) => write.row);
  return subject?.row ?? {};
}

// src/review/kinds.ts
var VOCABULARY_KINDS = /* @__PURE__ */ new Set(["entity", "label", "tag", "group"]);
var EDGE_KINDS = /* @__PURE__ */ new Set(["assignment", "entity_mapping"]);

// src/review/displayName.ts
var BOUNDARIES = [/([a-z0-9])([A-Z])/g, /([A-Z])([A-Z][a-z])/g];
function humanizeName(raw) {
  if (!raw) return "";
  let text2 = raw.trim().replace(/_/g, " ");
  for (const boundary of BOUNDARIES) text2 = text2.replace(boundary, "$1 $2");
  return text2.split(/\s+/).filter(Boolean).join(" ");
}

// src/review/candidateTitle.ts
function nameIndex(items) {
  const index = /* @__PURE__ */ new Map();
  const add = (row) => {
    const id = asTrimmedString(row?.id);
    const name = asTrimmedString(row?.name);
    if (id && name) index.set(id, name);
  };
  for (const item of items) {
    const bundle = catalogBundle(item);
    if (bundle) {
      for (const write of bundle.writes) {
        if (write.kind && VOCABULARY_KINDS.has(write.kind)) add(write.row);
      }
      continue;
    }
    if (VOCABULARY_KINDS.has(item.target_kind)) add(item.payload);
  }
  return index;
}
var KIND_LABELS = {
  address: "Address",
  token: "Token",
  nft: "NFT",
  abi: "ABI",
  proxy: "Proxy",
  entity: "Entity",
  label: "Label",
  tag: "Tag",
  group: "Group",
  assignment: "Assignment",
  entity_mapping: "Entity mapping",
  asset_ticker: "Ticker"
};
function kindLabel(kind) {
  return KIND_LABELS[kind] ?? kind.replace(/_/g, " ");
}
function candidateTitle(item, names2) {
  const payload = subjectRow(item);
  if (VOCABULARY_KINDS.has(item.target_kind)) {
    const name = asTrimmedString(payload.name);
    if (name) return { title: humanizeName(name), opaque: false };
  }
  const referenced = asTrimmedString(payload.catalog_id) ?? asTrimmedString(payload.entity_id);
  if (referenced) {
    const name = names2?.get(referenced);
    const prefix = asTrimmedString(payload.catalog_kind);
    if (name) {
      const label = humanizeName(name);
      return {
        title: prefix ? `${kindLabel(prefix)} \xB7 ${label}` : label,
        opaque: false
      };
    }
    return { title: prefix ? kindLabel(prefix) : kindLabel(item.target_kind), opaque: true };
  }
  const own = asTrimmedString(payload.name) ?? asTrimmedString(payload.symbol);
  if (own) return { title: humanizeName(own), opaque: false };
  return { title: kindLabel(item.target_kind), opaque: true };
}
var NO_NAMES = /* @__PURE__ */ new Map();
var RISK_LEVELS = new Set(Object.values(RiskLevel));
var SEVERITIES = new Set(Object.values(Severity));
function asRisk(row) {
  const v = strField(row, "risk_level");
  return v && RISK_LEVELS.has(v) ? v : void 0;
}
function asSeverity(row) {
  const v = strField(row, "severity");
  return v && SEVERITIES.has(v) ? v : void 0;
}
function strField(row, ...keys) {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === "string" && v.trim()) return v;
  }
  return void 0;
}
function bool(row, key) {
  const v = row[key];
  return typeof v === "boolean" ? v : void 0;
}
function strList(row, ...keys) {
  for (const k of keys) {
    const v = row[k];
    if (Array.isArray(v)) return v.filter((x) => typeof x === "string" && !!x.trim());
  }
  return [];
}
function httpLogo(row, ...keys) {
  const v = strField(row, ...keys);
  return v && /^https?:\/\//i.test(v) ? v : void 0;
}
function renderSource(item) {
  const payload = item.payload;
  if (payload && typeof payload === "object" && Object.keys(payload).length > 0) return payload;
  const proposed = item.proposed_value;
  if (proposed && typeof proposed === "object" && !Array.isArray(proposed)) return proposed;
  return null;
}
function CandidatePreview({ item }) {
  return /* @__PURE__ */ jsx(PreviewBoundary, { children: /* @__PURE__ */ jsx(CandidateBody, { item }) });
}
function CandidateBody({ item }) {
  const bundle = React.useMemo(() => catalogBundle(item), [item]);
  if (bundle) return /* @__PURE__ */ jsx(BundlePreview, { bundle });
  const row = renderSource(item);
  if (!row) {
    return item.field_path ? /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "No structured payload on this candidate to preview." }) : /* @__PURE__ */ jsx(NoReadableFields, {});
  }
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
    /* @__PURE__ */ jsx(RowPreview, { kind: item.target_kind, row, fallbackId: item.target_id }),
    /* @__PURE__ */ jsx(RowFieldsToggle, { row })
  ] });
}
function NoReadableFields() {
  return /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "This proposal carries no readable fields, so what approving it would write cannot be shown here. Leave it pending." });
}
var PreviewBoundary = class extends React.Component {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (this.state.failed) {
      return /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "Couldn\u2019t render this candidate with the product components \u2014 its fields are unreadable here, so leave it pending or inspect the raw payload." });
    }
    return this.props.children;
  }
};
function BundlePreview({ bundle }) {
  const count = bundle.writes.length;
  if (count === 0) return /* @__PURE__ */ jsx(NoReadableFields, {});
  return /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
    !bundle.readable ? /* @__PURE__ */ jsx(NoReadableFields, {}) : null,
    /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: count === 1 ? "Approving writes this row:" : (
      // One transaction in `_write_catalog_bundle`: a refusal of any row
      // refuses them all, so none can land without the others.
      `Approving writes these ${count} rows together \u2014 all of them or none:`
    ) }),
    bundle.writes.map((write, index) => /* @__PURE__ */ jsx(BundleWriteCard, { write, names: bundle.names }, `${write.table}:${index}`)),
    bundle.tenancy.length > 0 ? /* @__PURE__ */ jsx("div", { className: "rounded-md border", children: /* @__PURE__ */ jsx(Disclosure, { title: "Tenancy it is written under", count: bundle.tenancy.length, children: /* @__PURE__ */ jsx(FieldList, { fields: bundle.tenancy, raw: true }) }) }) : null
  ] });
}
function BundleWriteCard({ write, names: names2 }) {
  return /* @__PURE__ */ jsxs("div", { className: "min-w-0 rounded-md border", children: [
    /* @__PURE__ */ jsx("div", { className: "flex flex-wrap items-center gap-2 border-b px-3 py-1.5", children: /* @__PURE__ */ jsx(
      Badge,
      {
        variant: write.kind ? "secondary" : "destructive",
        className: "font-normal",
        title: write.table || void 0,
        children: write.label
      }
    ) }),
    /* @__PURE__ */ jsxs("div", { className: "space-y-3 px-3 py-3", children: [
      !write.kind ? /* @__PURE__ */ jsxs("p", { className: "text-xs text-destructive", children: [
        write.table ? "Not a catalog table an approval can write" : "This write names no table",
        " ",
        "\u2014 approving the proposal will be refused."
      ] }) : null,
      write.tenancyMismatch.length > 0 ? /* @__PURE__ */ jsxs("p", { className: "text-xs text-destructive", children: [
        "This row\u2019s ",
        write.tenancyMismatch.join(", "),
        " differs from the proposal\u2019s tenancy \u2014 approving will be refused."
      ] }) : null,
      write.row === null ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "This write carries no readable row." }) : write.fields.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "This row carries only keys, tenancy and empty fields \u2014 nothing a reviewer can read." }) : write.kind ? /* @__PURE__ */ jsx(RowPreview, { kind: write.kind, row: write.row, names: names2 }) : /* @__PURE__ */ jsx(FieldList, { fields: write.fields })
    ] }),
    write.row !== null && write.fields.length + write.hidden.length > 0 ? /* @__PURE__ */ jsx(AllFields, { fields: write.fields, hidden: write.hidden }) : null
  ] });
}
function RowPreview({
  kind,
  row,
  fallbackId,
  names: names2 = NO_NAMES
}) {
  switch ((kind || "").toLowerCase()) {
    case "address":
      return /* @__PURE__ */ jsx(AddressPreview, { row, fallbackId });
    case "entity":
      return /* @__PURE__ */ jsx(EntityPreview, { row });
    case "token":
      return /* @__PURE__ */ jsx(TokenPreview, { row });
    case "label":
      return /* @__PURE__ */ jsx(LabelPreview, { row });
    case "tag":
      return /* @__PURE__ */ jsx(TagPreview, { row });
    case "group":
      return /* @__PURE__ */ jsx(GroupPreview, { row, names: names2 });
    case "proxy":
      return /* @__PURE__ */ jsx(ProxyPreview, { row, fallbackId });
    case "assignment":
      return /* @__PURE__ */ jsx(AssignmentPreview, { row, fallbackId, names: names2 });
    case "entity_mapping":
      return /* @__PURE__ */ jsx(EntityMappingPreview, { row, fallbackId, names: names2 });
    default:
      return /* @__PURE__ */ jsx(GenericPreview, { row });
  }
}
function RowFieldsToggle({ row }) {
  const { fields, hidden } = React.useMemo(() => rowFields(row), [row]);
  if (fields.length + hidden.length === 0) return null;
  return /* @__PURE__ */ jsx("div", { className: "rounded-md border", children: /* @__PURE__ */ jsx(AllFields, { fields, hidden }) });
}
function BadgeRow({ children }) {
  return /* @__PURE__ */ jsx("div", { className: "flex flex-wrap items-center gap-1.5", children });
}
function AddressPreview({ row, fallbackId }) {
  const address = addressPart(strField(row, "address")) ?? addressPart(fallbackId) ?? "";
  const chain = strField(row, "chain", "chain_id");
  const name = strField(row, "name", "label");
  const type = strField(row, "address_type");
  const lifecycle = strField(row, "lifecycle");
  const risk = asRisk(row);
  const primary = bool(row, "is_primary_chain");
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
    /* @__PURE__ */ jsx(AddressDisplay, { address, label: name, chain, copyable: true, showExplorerIcon: true }),
    /* @__PURE__ */ jsxs(BadgeRow, { children: [
      type ? /* @__PURE__ */ jsx(AddressTypeBadge, { type }) : null,
      primary != null ? /* @__PURE__ */ jsx(PrimaryChainBadge, { primary, showWhenFalse: true }) : null,
      lifecycle ? /* @__PURE__ */ jsx(LifecycleBadge, { lifecycle }) : null,
      risk ? /* @__PURE__ */ jsx(RiskBadge, { level: risk }) : null
    ] }),
    /* @__PURE__ */ jsx(IdentityFacts, { row, keys: ["symbol", "decimals", "primary_entity_id"] })
  ] });
}
function EntityPreview({ row }) {
  const name = strField(row, "name") ?? "(unnamed entity)";
  const lifecycle = strField(row, "lifecycle");
  const risk = asRisk(row);
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
    /* @__PURE__ */ jsx(
      EntityCard,
      {
        name,
        entityType: strField(row, "entity_type"),
        description: strField(row, "description"),
        avatarUrl: httpLogo(row, "logo_url", "logo_s3_uri"),
        tags: strList(row, "tags")
      }
    ),
    /* @__PURE__ */ jsxs(BadgeRow, { children: [
      lifecycle ? /* @__PURE__ */ jsx(LifecycleBadge, { lifecycle }) : null,
      risk ? /* @__PURE__ */ jsx(RiskBadge, { level: risk }) : null
    ] }),
    /* @__PURE__ */ jsx(IdentityFacts, { row, keys: ["applies_to_chains", "aliases", "website", "twitter", "color"] })
  ] });
}
function TokenPreview({ row }) {
  const symbol = strField(row, "symbol");
  const name = strField(row, "name");
  const chain = strField(row, "chain_id", "chain");
  const verified = bool(row, "is_verified");
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx("span", { className: "text-base font-semibold", children: symbol ?? name ?? "(token)" }),
      name && symbol ? /* @__PURE__ */ jsx("span", { className: "text-sm text-muted-foreground", children: name }) : null,
      chain ? /* @__PURE__ */ jsx(ChainAvatar, { chain, size: "sm" }) : null
    ] }),
    /* @__PURE__ */ jsx(BadgeRow, { children: verified != null ? /* @__PURE__ */ jsx(LifecycleBadge, { lifecycle: verified ? "verified" : "candidate" }) : null }),
    /* @__PURE__ */ jsx(IdentityFacts, { row, keys: ["identifier", "decimals", "category"] })
  ] });
}
function LabelPreview({ row }) {
  const lifecycle = strField(row, "lifecycle");
  const risk = asRisk(row);
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
    /* @__PURE__ */ jsx(
      CatalogLabel,
      {
        label: strField(row, "name") ?? "(label)",
        scope: "provider",
        showScope: true,
        logo: httpLogo(row, "icon"),
        color: strField(row, "color")
      }
    ),
    /* @__PURE__ */ jsxs(BadgeRow, { children: [
      lifecycle ? /* @__PURE__ */ jsx(LifecycleBadge, { lifecycle }) : null,
      risk ? /* @__PURE__ */ jsx(RiskBadge, { level: risk }) : null
    ] }),
    /* @__PURE__ */ jsx(
      IdentityFacts,
      {
        row,
        keys: ["description", "severity", "address", "chain", "applies_to_chains"]
      }
    )
  ] });
}
function TagPreview({ row }) {
  const lifecycle = strField(row, "lifecycle");
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
    /* @__PURE__ */ jsx(
      Tag,
      {
        label: strField(row, "name") ?? "(tag)",
        severity: asSeverity(row),
        logo: httpLogo(row, "icon"),
        color: strField(row, "color")
      }
    ),
    /* @__PURE__ */ jsx(BadgeRow, { children: lifecycle ? /* @__PURE__ */ jsx(LifecycleBadge, { lifecycle }) : null }),
    /* @__PURE__ */ jsx(IdentityFacts, { row, keys: ["description", "category", "applies_to_chains"] })
  ] });
}
function GroupPreview({ row, names: names2 }) {
  const members = ["member_entity_ids", "member_label_ids", "member_tag_ids", "member_transaction_group_ids"].map((key) => strList(row, key).length).reduce((total, n) => total + n, 0);
  const lifecycle = strField(row, "lifecycle");
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
    /* @__PURE__ */ jsx(
      GroupBadge,
      {
        groupId: strField(row, "id") ?? strField(row, "name") ?? "group",
        name: strField(row, "name") ?? "(group)",
        count: members || void 0
      }
    ),
    /* @__PURE__ */ jsx(BadgeRow, { children: lifecycle ? /* @__PURE__ */ jsx(LifecycleBadge, { lifecycle }) : null }),
    /* @__PURE__ */ jsx(
      IdentityFacts,
      {
        row,
        names: names2,
        keys: ["description", "applies_to_chains", "member_entity_ids", "member_label_ids", "member_tag_ids"]
      }
    )
  ] });
}
function ProxyPreview({ row, fallbackId }) {
  const address = addressPart(strField(row, "address")) ?? addressPart(fallbackId) ?? "";
  const chain = strField(row, "chain", "chain_id");
  const pattern = strField(row, "pattern");
  const impls = strList(row, "implementations").map((v) => addressPart(v) ?? v);
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
    /* @__PURE__ */ jsx(
      AddressDisplay,
      {
        address,
        label: pattern ? `${pattern} proxy` : "proxy contract",
        chain,
        copyable: true,
        showExplorerIcon: true
      }
    ),
    /* @__PURE__ */ jsx(BadgeRow, { children: pattern ? /* @__PURE__ */ jsx(Tag, { label: pattern }) : null }),
    impls.length ? /* @__PURE__ */ jsxs("div", { className: "space-y-1.5", children: [
      /* @__PURE__ */ jsxs("p", { className: "text-[11px] uppercase tracking-wide text-muted-foreground", children: [
        "Delegates to implementation",
        impls.length > 1 ? "s" : ""
      ] }),
      impls.map((impl) => /* @__PURE__ */ jsx(AddressDisplay, { address: impl, chain, copyable: true, showExplorerIcon: true }, impl))
    ] }) : null,
    /* @__PURE__ */ jsx(IdentityFacts, { row, keys: ["is_proxy", "pattern"] })
  ] });
}
function AssignmentPreview({
  row,
  fallbackId,
  names: names2
}) {
  const scope = strField(row, "scope");
  const target = strField(row, "scope_target_id") ?? "";
  const chain = strField(row, "chain_id", "chain");
  const catalogKind = strField(row, "catalog_kind");
  const catalogName = bareName(names2.get(strField(row, "catalog_id") ?? ""));
  const targetAddr = addressPart(target) ?? addressPart(fallbackId) ?? target;
  const isAddress = scope === "address" || target.includes(":") || /^0x/i.test(target);
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
    /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
      "Attaches ",
      catalogKind ?? "catalog row",
      " ",
      catalogName ? /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: catalogName }) : null,
      " \u2192",
      " ",
      scope ?? "target"
    ] }),
    isAddress ? /* @__PURE__ */ jsx(AddressDisplay, { address: targetAddr, chain, copyable: true, showExplorerIcon: true }) : /* @__PURE__ */ jsx("span", { className: "break-all font-mono text-sm", children: target || "(target)" }),
    /* @__PURE__ */ jsx(
      IdentityFacts,
      {
        row,
        names: names2,
        keys: ["catalog_id", "chain_id", "confidence", "source", "valid_from", "is_human_approved"]
      }
    )
  ] });
}
function EntityMappingPreview({
  row,
  fallbackId,
  names: names2
}) {
  const address = addressPart(strField(row, "address")) ?? addressPart(fallbackId) ?? "";
  const chain = strField(row, "chain", "chain_id");
  const role = strField(row, "role");
  const entityName = bareName(names2.get(strField(row, "entity_id") ?? ""));
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
    /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
      "Maps address \u2192",
      " ",
      entityName ? /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: entityName }) : "entity"
    ] }),
    /* @__PURE__ */ jsx(AddressDisplay, { address, chain, copyable: true, showExplorerIcon: true }),
    /* @__PURE__ */ jsx(BadgeRow, { children: role ? /* @__PURE__ */ jsx(Tag, { label: role }) : null }),
    /* @__PURE__ */ jsx(
      IdentityFacts,
      {
        row,
        names: names2,
        keys: ["entity_id", "chain", "tier", "confidence", "valid_from"]
      }
    )
  ] });
}
function GenericPreview({ row }) {
  return /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
    /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "No visual renderer for this kind \u2014 showing key fields." }),
    /* @__PURE__ */ jsx(IdentityFacts, { row, keys: Object.keys(row).slice(0, 12) })
  ] });
}
function IdentityFacts({
  row,
  keys,
  names: names2 = NO_NAMES
}) {
  const facts = keys.map((k) => [k, row[k]]).filter(
    ([k, v]) => k === "applies_to_chains" && Array.isArray(v) || v != null && v !== "" && !(Array.isArray(v) && v.length === 0)
  );
  if (facts.length === 0) return null;
  return /* @__PURE__ */ jsx("div", { className: "grid gap-x-6 gap-y-1.5 rounded-md border p-3 sm:grid-cols-2", children: facts.map(([k, v]) => {
    const shown = displayFieldValue(k, v, names2);
    const raw = formatFieldValue(v);
    return /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-col gap-0.5", children: [
      /* @__PURE__ */ jsx("span", { className: "text-[11px] uppercase tracking-wide text-muted-foreground", title: k, children: fieldLabel(k) }),
      /* @__PURE__ */ jsx("span", { className: "text-xs wrap-anywhere", title: shown !== raw ? raw : void 0, children: shown })
    ] }, k);
  }) });
}
function Disclosure({
  title,
  count,
  children
}) {
  return /* @__PURE__ */ jsxs("details", { className: "group/fields", children: [
    /* @__PURE__ */ jsxs("summary", { className: "flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-1.5 text-xs text-muted-foreground", children: [
      /* @__PURE__ */ jsxs("span", { children: [
        title,
        " (",
        count,
        ")"
      ] }),
      /* @__PURE__ */ jsx("span", { className: "group-open/fields:hidden", children: "Show" }),
      /* @__PURE__ */ jsx("span", { className: "hidden group-open/fields:inline", children: "Hide" })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "space-y-3 border-t px-3 py-2", children })
  ] });
}
function AllFields({ fields, hidden }) {
  return /* @__PURE__ */ jsx("div", { className: "border-t", children: /* @__PURE__ */ jsxs(Disclosure, { title: "All fields, keys and tenancy", count: fields.length + hidden.length, children: [
    fields.length > 0 ? /* @__PURE__ */ jsx(FieldList, { fields }) : null,
    hidden.length > 0 ? /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsx("p", { className: "text-[11px] uppercase tracking-wide text-muted-foreground", children: "Keys, tenancy and empty fields" }),
      /* @__PURE__ */ jsx(FieldList, { fields: hidden, raw: true })
    ] }) : null
  ] }) });
}
function FieldList({ fields, raw = false }) {
  return /* @__PURE__ */ jsx("dl", { className: "grid grid-cols-[minmax(6rem,max-content)_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs", children: fields.map((field) => {
    const verbatim = formatFieldValue(field.value);
    return /* @__PURE__ */ jsxs(React.Fragment, { children: [
      /* @__PURE__ */ jsx(
        "dt",
        {
          className: raw ? "font-mono text-muted-foreground" : "text-muted-foreground",
          title: raw ? void 0 : field.field,
          children: raw ? field.field : field.label
        }
      ),
      /* @__PURE__ */ jsx(
        "dd",
        {
          className: raw ? "break-all font-mono" : "wrap-anywhere",
          title: !raw && field.display !== verbatim ? verbatim : void 0,
          children: raw ? verbatim : field.display
        }
      )
    ] }, field.field);
  }) });
}
function addressPart(value) {
  if (!value) return void 0;
  const seg = value.includes(":") ? value.slice(value.lastIndexOf(":") + 1) : value;
  return seg || void 0;
}
var TIER_TONES = {
  high: "text-emerald-600",
  medium: "text-sky-600",
  low: "text-amber-600",
  weak: "text-muted-foreground"
};
function tone(confidence) {
  return TIER_TONES[trustTier(confidence)];
}
function fmt(n, digits = 2) {
  return n == null || !Number.isFinite(n) ? "\u2014" : n.toFixed(digits);
}
function plural(n, word) {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}
function EvidencePanel({
  evidence,
  confidence
}) {
  const ev = summariseEvidence(evidence, confidence);
  if (!ev.hasBreakdown) return null;
  return /* @__PURE__ */ jsxs("section", { className: "space-y-2", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-baseline justify-between gap-2", children: [
      /* @__PURE__ */ jsx("p", { className: "text-xs font-medium text-muted-foreground", children: "Why this score" }),
      ev.hasArithmetic ? (
        // The arithmetic, spelled out, so the headline is falsifiable.
        /* @__PURE__ */ jsxs("p", { className: "font-mono text-[11px] text-muted-foreground", children: [
          fmt(ev.base),
          /* @__PURE__ */ jsxs("span", { className: "mx-1", children: [
            ev.delta < 0 ? "\u2212" : "+",
            fmt(Math.abs(ev.delta))
          ] }),
          "=",
          /* @__PURE__ */ jsx("span", { className: "ml-1 text-sm font-semibold text-foreground", children: fmt(ev.score) })
        ] })
      ) : null
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "space-y-2 rounded-md border p-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-1.5", children: [
        /* @__PURE__ */ jsx(Badge, { variant: "secondary", children: plural(ev.sourceCount, "independent source") }),
        ev.delta ? /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "border-emerald-500/40 text-emerald-600", children: [
          ev.delta > 0 ? "+" : "\u2212",
          fmt(Math.abs(ev.delta)),
          " agreement"
        ] }) : null,
        ev.conflicts ? /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "border-destructive/40 text-destructive", children: [
          /* @__PURE__ */ jsx(AlertIcon, { className: "mr-1 h-3 w-3" }),
          plural(ev.conflicts, "conflict")
        ] }) : null
      ] }),
      /* @__PURE__ */ jsx("ul", { className: "divide-y", children: ev.sources.map((source, i) => /* @__PURE__ */ jsx(SourceRow, { source }, `${source.kind}:${source.id ?? i}`)) }),
      ev.explanation ? /* @__PURE__ */ jsx("p", { className: "text-[11px] leading-relaxed text-muted-foreground", children: ev.explanation }) : null
    ] })
  ] });
}
function SourceRow({ source }) {
  const fields = source.asserted_fields ?? [];
  const age = Math.round(source.age_days ?? 0);
  const explainLines = explainOf(source);
  return /* @__PURE__ */ jsxs("li", { className: "flex flex-wrap items-baseline gap-x-2 gap-y-1 py-1.5 first:pt-0 last:pb-0", children: [
    /* @__PURE__ */ jsx("span", { className: `font-mono text-xs font-semibold ${tone(source.confidence)}`, children: fmt(source.confidence) }),
    /* @__PURE__ */ jsx("span", { className: "text-sm", children: source.id ?? source.kind }),
    source.url ? /* @__PURE__ */ jsx(
      "a",
      {
        href: source.url,
        target: "_blank",
        rel: "noreferrer noopener",
        className: "text-muted-foreground hover:text-foreground",
        "aria-label": `Open ${source.id ?? source.kind}`,
        children: /* @__PURE__ */ jsx(ExternalLinkIcon, { className: "h-3 w-3" })
      }
    ) : null,
    source.conflict ? /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "border-destructive/40 text-destructive", children: "disagrees" }) : null,
    isStale(source) ? /* @__PURE__ */ jsxs(Tooltip, { children: [
      /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "border-amber-500/40 text-amber-600", children: [
        age,
        "d old"
      ] }) }),
      /* @__PURE__ */ jsxs(TooltipContent, { children: [
        "Last seen ",
        age,
        " days ago \u2014 confidence decays with age."
      ] })
    ] }) : null,
    fields.length ? /* @__PURE__ */ jsxs("span", { className: "font-mono text-[11px] text-muted-foreground", children: [
      "asserts ",
      fields.join(", ")
    ] }) : null,
    explainLines.length ? /* @__PURE__ */ jsx("span", { className: "w-full text-[11px] text-muted-foreground", children: explainLines.join(" \xB7 ") }) : null
  ] });
}
function ConfidenceChip({
  confidence,
  evidence
}) {
  const ev = summariseEvidence(evidence, confidence);
  if (ev.unscored) {
    const why = UNSCORED_REASON_TEXT[ev.unscored];
    return /* @__PURE__ */ jsxs("span", { className: "text-xs text-muted-foreground", title: `Confidence ${why}.`, children: [
      "Unscored ",
      /* @__PURE__ */ jsxs("span", { className: "text-muted-foreground/70", children: [
        "(",
        why,
        ")"
      ] })
    ] });
  }
  const chip = /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
    /* @__PURE__ */ jsx("span", { className: `font-mono text-xs font-semibold ${tone(ev.score)}`, children: fmt(ev.score) }),
    ev.hasBreakdown ? /* @__PURE__ */ jsxs("span", { className: "text-[11px] text-muted-foreground", children: [
      "\xD7",
      ev.sourceCount
    ] }) : null,
    ev.conflicts ? /* @__PURE__ */ jsx(AlertIcon, { className: "h-3 w-3 text-destructive" }) : null
  ] });
  if (!hasEvidence(evidence)) return chip;
  return /* @__PURE__ */ jsxs(Tooltip, { children: [
    /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: chip }),
    /* @__PURE__ */ jsxs(TooltipContent, { children: [
      /* @__PURE__ */ jsxs("span", { className: "block", children: [
        plural(ev.sourceCount, "independent source"),
        ev.conflicts ? `, ${plural(ev.conflicts, "conflict")}` : ""
      ] }),
      ev.strongest ? /* @__PURE__ */ jsxs("span", { className: "block opacity-80", children: [
        "strongest: ",
        ev.strongest.id ?? ev.strongest.kind,
        " (",
        fmt(ev.strongest.confidence),
        ")"
      ] }) : null
    ] })
  ] });
}
function fmtScalar(value) {
  if (value == null) return "\u2014";
  if (Array.isArray(value)) return value.map((x) => String(x)).join(", ") || "\u2014";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}
function ReviewDiff({ item, preservedHint }) {
  if (item.field_path) {
    return /* @__PURE__ */ jsxs("section", { className: "space-y-2", children: [
      /* @__PURE__ */ jsxs("p", { className: "text-xs font-medium text-muted-foreground", children: [
        "Proposed change \xB7 ",
        /* @__PURE__ */ jsx("span", { className: "font-mono", children: item.field_path })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "grid gap-3 sm:grid-cols-2", children: [
        /* @__PURE__ */ jsx(DiffBox, { tone: "border-destructive/40", title: "Existing", value: item.existing_value }),
        /* @__PURE__ */ jsx(DiffBox, { tone: "border-emerald-500/40", title: "Proposed", value: item.proposed_value })
      ] })
    ] });
  }
  return /* @__PURE__ */ jsx(RowDiffPanel, { item, preservedHint });
}
function RowDiffPanel({ item, preservedHint }) {
  const diff = React.useMemo(() => rowDiff(item), [item]);
  if (diff.unknown) return null;
  if (diff.changes.length === 0 && diff.preserved.length === 0) return null;
  return /* @__PURE__ */ jsxs("section", { className: "space-y-2", children: [
    /* @__PURE__ */ jsx("p", { className: "text-xs font-medium text-muted-foreground", children: diff.changes.length === 0 ? "Approving changes nothing on the existing row" : `Approving overwrites ${diff.changes.length} field${diff.changes.length === 1 ? "" : "s"} on the existing row` }),
    diff.changes.length > 0 ? /* @__PURE__ */ jsx("div", { className: "overflow-x-auto rounded-md border", children: /* @__PURE__ */ jsxs("table", { className: "w-full text-xs", children: [
      /* @__PURE__ */ jsx("thead", { className: "bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground", children: /* @__PURE__ */ jsxs("tr", { children: [
        /* @__PURE__ */ jsx("th", { className: "px-3 py-1.5 text-left font-medium", children: "Field" }),
        /* @__PURE__ */ jsx("th", { className: "px-3 py-1.5 text-left font-medium", children: "Existing" }),
        /* @__PURE__ */ jsx("th", { className: "px-3 py-1.5 text-left font-medium", children: "Proposed" })
      ] }) }),
      /* @__PURE__ */ jsx("tbody", { className: "divide-y", children: diff.changes.map((change) => /* @__PURE__ */ jsxs("tr", { className: "align-top", children: [
        /* @__PURE__ */ jsx("td", { className: "px-3 py-1.5 font-mono text-[11px]", children: change.field }),
        /* @__PURE__ */ jsx("td", { className: "px-3 py-1.5 text-destructive", children: change.omitted ? /* @__PURE__ */ jsx("span", { className: "italic text-muted-foreground", children: "too large to show" }) : /* @__PURE__ */ jsx(ScalarOrJson, { value: change.existing }) }),
        /* @__PURE__ */ jsx("td", { className: "px-3 py-1.5 text-emerald-600", children: /* @__PURE__ */ jsx(ScalarOrJson, { value: change.proposed }) })
      ] }, change.field)) })
    ] }) }) : null,
    diff.preserved.length > 0 ? /* @__PURE__ */ jsxs("p", { className: "text-[11px] text-muted-foreground", children: [
      "Kept as-is (a curated value or the row's identity, which an un-edited proposal doesn't overwrite): ",
      /* @__PURE__ */ jsx("span", { className: "font-mono", children: diff.preserved.join(", ") }),
      ".",
      preservedHint ? /* @__PURE__ */ jsxs(Fragment, { children: [
        " ",
        preservedHint
      ] }) : null
    ] }) : null
  ] });
}
function ScalarOrJson({ value }) {
  if (value === void 0) return /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "\u2014" });
  const text2 = value === null || typeof value !== "object" ? String(value) : JSON.stringify(value);
  return /* @__PURE__ */ jsx("span", { className: "break-all font-mono text-[11px]", children: text2.length > 160 ? `${text2.slice(0, 160)}\u2026` : text2 });
}
function DiffBox({
  title,
  value,
  tone: tone2
}) {
  return /* @__PURE__ */ jsxs("div", { className: "min-w-0 space-y-1", children: [
    /* @__PURE__ */ jsx("p", { className: "text-[11px] uppercase tracking-wide text-muted-foreground", children: title }),
    /* @__PURE__ */ jsx("pre", { className: `overflow-auto rounded-md border ${tone2} bg-muted/30 p-2 text-[11px]`, children: fmtScalar(value) })
  ] });
}
function ChainTag({ chain, bare, className }) {
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: bare ? `inline-flex items-center gap-1.5 ${className ?? ""}` : `${badgeVariants({ variant: "outline" })} gap-1.5 pl-1 text-[11px] font-normal ${className ?? ""}`,
      children: [
        /* @__PURE__ */ jsx(ChainAvatar, { chain, size: "sm", showName: false }),
        /* @__PURE__ */ jsx("span", { title: chain, children: chainDisplayName(chain) })
      ]
    }
  );
}
function ChainTags({
  chains,
  max = 3,
  className
}) {
  if (chains.length === 0) return null;
  const shown = chains.slice(0, max);
  const rest = chains.slice(max);
  return /* @__PURE__ */ jsxs("span", { className: `inline-flex flex-wrap items-center gap-1 ${className ?? ""}`, children: [
    shown.map((c) => /* @__PURE__ */ jsx(ChainTag, { chain: c }, c)),
    rest.length > 0 ? /* @__PURE__ */ jsxs(
      "span",
      {
        className: `${badgeVariants({ variant: "secondary" })} text-[11px] font-normal`,
        title: rest.map((c) => chainDisplayName(c)).join(", "),
        children: [
          "+",
          rest.length
        ]
      }
    ) : null
  ] });
}

export { CATALOG_WRITE_TABLES, CandidatePreview, ChainTag, ChainTags, ConfidenceChip, EDGE_KINDS, EvidencePanel, REVIEW_REASONS, REVIEW_STATUSES, REVIEW_STATUS_FILTERS, REVIEW_TARGET_KINDS, ReviewDiff, STALE_AFTER_DAYS, UNSCORED_REASON_TEXT, VOCABULARY_KINDS, bareName, candidateTitle, catalogBundle, displayFieldValue, explainOf, fieldLabel, formatFieldValue, hasEvidence, humanizeName, isRecord, isStale, nameIndex, reviewEvidenceFromWire, reviewItemFromWire, rowDiff, rowFields, subjectRow, summariseEvidence, trustTier };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map